/**
 * #2712 CLIENTERROR: what a browser error report's message may say. One list,
 * held by both sides: the client reporter (src/client/lib/client-error-report.ts)
 * writes messages from it, and normaliseClientErrorReport
 * (src/shared/client-error.ts), which the client and POST /api/client-error
 * both run, logs any other message as MESSAGE_NOT_ALLOWED.
 *
 * Free text can carry a person's name, and neither a denylist nor the error's
 * class can prove it does not (an engine writes `(reading 'Jo Bloggs')`; an
 * app can throw its own `TypeError`). So a message travels only as one of
 * SAFE_MESSAGES: fixed engine, browser, React and chunk-load wording whose
 * every filled-in part is cut to "…". Review (bc0a14742, High): the list ran
 * only in the browser, so any signed-in caller could POST any text into the
 * Worker log. The server now holds every report to the same list.
 *
 * Browser-safe: no lookbehind, no Node APIs.
 */

import CLIENT_ERROR_SETS from "./client-error-sets.v1.json";

/** An error's own text that the report does not carry. */
export const WITHHELD = "[message withheld]";

/** Logged in place of a message that is not on the list (a client that is not this app's reporter). */
export const MESSAGE_NOT_ALLOWED = "[message not allowed]";

/**
 * [pattern, output]. Each pattern is anchored and matches one engine's whole
 * message; each output is literal words, with $1 only where the pattern's
 * group is itself a fixed alternative (undefined|null) or digits. V8 is
 * Chrome and Edge, JavaScriptCore Safari, SpiderMonkey Firefox. Every output
 * maps to itself (isAllowedMessage relies on it; a test pins it per entry).
 */
export const SAFE_MESSAGES: ReadonlyArray<readonly [RegExp, string]> = [
  // V8
  [/^Cannot read properties of (undefined|null) \(reading '[^'\n]*'\)$/, "Cannot read properties of $1 (reading '…')"],
  [/^Cannot set properties of (undefined|null) \(setting '[^'\n]*'\)$/, "Cannot set properties of $1 (setting '…')"],
  [/^Cannot destructure property '[^'\n]*' of '[^'\n]*' as it is (undefined|null)\.$/, "Cannot destructure property '…' of '…' as it is $1."],
  [/^Cannot destructure '[^'\n]*' as it is (undefined|null)\.$/, "Cannot destructure '…' as it is $1."],
  [/^Cannot access '[^'\n]*' before initialization$/, "Cannot access '…' before initialization"],
  [/^Cannot convert undefined or null to object$/, "Cannot convert undefined or null to object"],
  [/^Assignment to constant variable\.$/, "Assignment to constant variable."],
  [/^Maximum call stack size exceeded\.?$/, "Maximum call stack size exceeded"],
  [/^Invalid time value$/, "Invalid time value"],
  [/^Invalid array length$/, "Invalid array length"],
  [/^Failed to fetch$/, "Failed to fetch"],
  // V8, JavaScriptCore and SpiderMonkey share these shapes; JavaScriptCore adds "(In '...')".
  [/^[^\n]+ is not a function(?:\. \(In [^\n]*\))?$/, "… is not a function"],
  [/^[^\n]+ is not a constructor(?:\. \(In [^\n]*\))?$/, "… is not a constructor"],
  [/^[^\n]+ is not iterable(?: \(cannot read property Symbol\(Symbol\.iterator\)\))?$/, "… is not iterable"],
  [/^[^\n]+ is not defined$/, "… is not defined"],
  // Every engine's JSON.parse wording echoes the text it choked on. The last
  // alternative is the output itself, so it maps to itself.
  [
    /^(?:Unexpected end of JSON input|Unexpected non-whitespace character after JSON\b|Expected [^\n]* in JSON\b|Bad control character [^\n]* in JSON\b|[^\n]*\bis not valid JSON\b|JSON\.parse: |JSON Parse error: |JSON\.parse failed$)/,
    "JSON.parse failed",
  ],
  // JavaScriptCore
  [/^(undefined|null) is not an object \(evaluating '[^\n]*'\)$/, "$1 is not an object (evaluating '…')"],
  [/^Can't find variable: [^\n]+$/, "Can't find variable: …"],
  [/^Load failed$/, "Load failed"],
  [/^(?:The user aborted a request\.|Fetch is aborted)$/, "The user aborted a request."],
  // SpiderMonkey
  [/^can't access property "[^"\n]*", [^\n]+ is (undefined|null)$/, 'can\'t access property "…", … is $1'],
  [/^can't access lexical declaration '[^'\n]*' before initialization$/, "can't access lexical declaration '…' before initialization"],
  [/^[^\n]+ has no properties$/, "… has no properties"],
  [/^[^\n]+ is (undefined|null)$/, "… is $1"],
  [/^too much recursion$/, "too much recursion"],
  [/^NetworkError when attempting to fetch resource\.$/, "NetworkError when attempting to fetch resource."],
  [/^invalid date$/i, "invalid date"],
  // DOM and the browser
  [/^(?:The operation was aborted\.?|signal is aborted without reason)$/, "The operation was aborted."],
  // Its two names look like WebIDL but an app can write the same words.
  [/^Failed to execute '[^'\n]*' on '[^'\n]*': [^\n]*$/, "Failed to execute '…' on '…': …"],
  [/^Script error\.?$/, "Script error."],
  [/^ResizeObserver loop completed with undelivered notifications\.$/, "ResizeObserver loop completed with undelivered notifications."],
  [/^ResizeObserver loop limit exceeded$/, "ResizeObserver loop limit exceeded"],
  // React (production) and chunk loading: the fixed head only.
  [/^Minified React error #(\d{1,4})\b[^\n]*$/, "Minified React error #$1"],
  [/^(Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed)\b[^\n]*$/i, "$1"],
  [/^Loading chunk (\d{1,6}) failed\b[^\n]*$/, "Loading chunk $1 failed"],
  [/^Loading CSS chunk \S+ failed\b[^\n]*$/, "Loading CSS chunk … failed"],
  [/^Unable to preload CSS\b[^\n]*$/, "Unable to preload CSS"],
];

/** A message longer than this is withheld unread: no template is that long. */
const SAFE_MESSAGE_MAX_CHARS = 1_000;

/**
 * The error names the engines throw (ECMAScript's, and SpiderMonkey's
 * InternalError) and the names a DOMException carries (WebIDL's table).
 */
export const BUILTIN_ERROR_NAMES = [
  "Error", "EvalError", "RangeError", "ReferenceError", "SyntaxError", "TypeError", "URIError", "AggregateError", "InternalError",
  "IndexSizeError", "HierarchyRequestError", "WrongDocumentError", "InvalidCharacterError", "NoModificationAllowedError",
  "NotFoundError", "NotSupportedError", "InUseAttributeError", "InvalidStateError", "InvalidModificationError", "NamespaceError",
  "InvalidAccessError", "TypeMismatchError", "SecurityError", "NetworkError", "AbortError", "URLMismatchError",
  "QuotaExceededError", "TimeoutError", "InvalidNodeTypeError", "DataCloneError", "EncodingError", "NotReadableError",
  "UnknownError", "ConstraintError", "DataError", "TransactionInactiveError", "ReadOnlyError", "VersionError",
  "OperationError", "NotAllowedError", "OptOutError",
] as const;

/**
 * The names a report may give an error: the engines' and DOMException's, and
 * the app's own error classes (generated from the browser's modules into
 * client-error-sets.v1.json; a test fails when it is stale). Review (round 2,
 * High): any name ending Error passed, so `JoBloggs0400000000Error` did too.
 */
export const KNOWN_ERROR_NAMES: ReadonlySet<string> = new Set<string>([...BUILTIN_ERROR_NAMES, ...CLIENT_ERROR_SETS.error_names]);

/** Logged in place of an error name that is not on KNOWN_ERROR_NAMES. */
export const ERROR_NAME_WITHHELD = "[error name withheld]";

/** The name a report gives an error whose `name` is `raw`: itself when known, "Error" when it has none. */
export function reportErrorName(raw: unknown): string {
  if (raw === undefined || raw === "") return "Error";
  return typeof raw === "string" && KNOWN_ERROR_NAMES.has(raw) ? raw : ERROR_NAME_WITHHELD;
}

const isLoggedName = (name: string) => KNOWN_ERROR_NAMES.has(name) || name === ERROR_NAME_WITHHELD;

/** The client's own code for the RBAC store's fail-closed 403, which says so in a sentence, not a code. */
export const RBAC_STORE_OUTAGE_CODE = "rbac_store_outage";

/**
 * The codes "API <status> (<code>)" may name: the machine codes the server's
 * responses carry (generated from src/server and src/shared into
 * client-error-sets.v1.json; a test fails when it is stale) and the client's
 * RBAC_STORE_OUTAGE_CODE. Review (round 2, High): any identifier passed, so
 * `API 500 (JoBloggs0400000000)` did too.
 */
export const KNOWN_API_CODES: ReadonlySet<string> = new Set<string>([...CLIENT_ERROR_SETS.api_codes, RBAC_STORE_OUTAGE_CODE]);

/** Named in place of a code that is not on KNOWN_API_CODES: "API 500 (code withheld)". */
export const API_CODE_WITHHELD = "code withheld";

/** "API 409", "API 503 (ostendo_unavailable)", or "API 500 (code withheld)" for a code the app does not define. */
export function apiSummary(status: number, code?: string): string {
  if (code === undefined) return `API ${status}`;
  return `API ${status} (${KNOWN_API_CODES.has(code) ? code : API_CODE_WITHHELD})`;
}

/** The template `text` fills, its captures cut; null when it fills none. */
export function safeMessage(text: string): string | null {
  if (text.length > SAFE_MESSAGE_MAX_CHARS) return null;
  for (const [pattern, output] of SAFE_MESSAGES) {
    const m = pattern.exec(text);
    if (m) return output.replace(/\$(\d)/g, (_, i: string) => m[Number(i)] ?? "");
  }
  return null;
}

// The shapes the reporter composes around a template or WITHHELD.
const API_SUMMARY = /^API \d{3}(?: \(([^()\n]*)\))?$/;
const NOT_AN_ERROR = /^Non-Error (?:(?:string|number|boolean|bigint|symbol|function|object): \[message withheld\]|null|undefined)$/;
const BROWSER_WITHHELD = `Browser message: ${WITHHELD}`;

/**
 * True when `message` is one the reporter writes:
 *   - "API <status>", or "API <status> (<code>)" with a code on KNOWN_API_CODES or API_CODE_WITHHELD;
 *   - a template's output, alone or after an error name ("TypeError: … is not a function");
 *   - "<Name>: [message withheld]", or "<Name>" alone for an error with no message;
 *     a name is one of KNOWN_ERROR_NAMES or ERROR_NAME_WITHHELD;
 *   - "Browser message: [message withheld]";
 *   - "Non-Error <type>: [message withheld]", "Non-Error null", "Non-Error undefined".
 * A template's output is recognised by mapping to itself, so a text with any
 * part filled in (`(reading 'Jo Bloggs')`) is not one.
 */
export function isAllowedMessage(message: string): boolean {
  const api = API_SUMMARY.exec(message);
  if (api) return api[1] === undefined || KNOWN_API_CODES.has(api[1]) || api[1] === API_CODE_WITHHELD;
  if (isLoggedName(message) || NOT_AN_ERROR.test(message) || message === BROWSER_WITHHELD) return true;
  const colon = message.indexOf(": ");
  const named = colon > 0 && isLoggedName(message.slice(0, colon));
  const text = named ? message.slice(colon + 2) : message;
  if (named && text === WITHHELD) return true;
  return safeMessage(text) === text;
}
